import { useEffect, useRef } from 'react';
import type { VideoSummary } from '@streamr/shared';
import { VideoCard } from './VideoCard';
import { EmptyState, ErrorState, Spinner } from './ui';

interface VideoGridProps {
  videos: VideoSummary[];
  isLoading: boolean;
  error: unknown;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  emptyTitle?: string;
  emptyHint?: string;
  showChannel?: boolean;
}

/**
 * Grid plus "load more". The sentinel is observed rather than bound to a scroll
 * handler so nested scroll containers work, and there is a real button behind
 * it: IntersectionObserver never fires for a keyboard user who tabs to the end
 * of the list, and an infinite feed with no reachable control is a dead end.
 */
export const VideoGrid = ({
  videos,
  isLoading,
  error,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  emptyTitle = 'Nothing here yet',
  emptyHint,
  showChannel = true,
}: VideoGridProps): JSX.Element => {
  const sentinel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinel.current;
    if (node === null || !hasNextPage || fetchNextPage === undefined) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting) && !isFetchingNextPage) fetchNextPage();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (error !== null && error !== undefined) return <ErrorState error={error} />;
  if (isLoading) return <Spinner label="Loading videos" />;
  if (videos.length === 0) return <EmptyState title={emptyTitle} {...(emptyHint !== undefined && { hint: emptyHint })} />;

  return (
    <>
      <div className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} showChannel={showChannel} />
        ))}
      </div>
      <div ref={sentinel} className="h-px" />
      {hasNextPage && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => fetchNextPage?.()}
            disabled={isFetchingNextPage}
            className="rounded-full border border-ink-600 px-5 py-2 text-sm text-ink-200 hover:bg-ink-700 disabled:opacity-60"
          >
            {isFetchingNextPage ? 'Loading...' : 'Load more'}
          </button>
        </div>
      )}
    </>
  );
};
